import { z } from 'zod';
import { cardSchema, isoDateSchema, passportSchema, phoneSchema, pinflSchema } from './common.js';
import { clientContactsSchema, conflictsWithClientPhone } from './contacts.js';
/**
 * MyID integratsiyasi — Inplace (new flow).
 * Manba: https://docs.myid.uz/#/ru/myid_inplace_new_flow
 *
 * ── Muhim tamoyil ──────────────────────────────────────────────────────────
 * Tashqi tizimdan **keladigan** ma'lumot yumshoq tekshiriladi: maydonlar
 * ixtiyoriy, ortiqcha maydonlar o'tkaziladi (`passthrough`). Aks holda MyID
 * javobiga bitta yangi maydon qo'shilishi bilan butun oqim to'xtab qolardi.
 * O'zimiz **qabul qiladigan** so'rovlar esa qat'iy tekshiriladi.
 */
/** MyID ba'zi identifikatorlarni son, ba'zilarini matn sifatida qaytaradi. */
const loose = z.union([z.string(), z.number()]).optional();
// ─── MyID qaytaradigan profil ───────────────────────────────────────────────
export const commonDataSchema = z
    .object({
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    middle_name: z.string().optional(),
    first_name_en: z.string().optional(),
    last_name_en: z.string().optional(),
    pinfl: z.string().optional(),
    /** 1 — erkak, 2 — ayol. MyID matn sifatida ham qaytarishi mumkin. */
    gender: z.coerce.number().int().optional(),
    birth_date: z.string().optional(),
    birth_place: z.string().optional(),
    birth_country: z.string().optional(),
    birth_country_id: loose,
    birth_country_id_cbu: loose,
    nationality: z.string().optional(),
    nationality_id: loose,
    nationality_id_cbu: loose,
    citizenship: z.string().optional(),
    citizenship_id: loose,
    citizenship_id_cbu: loose,
    /**
     * Takroriy murojaatda pasport va tug'ilgan sana o'rniga ishlatsa bo'ladi —
     * anketani qisqartiradi.
     */
    sdk_hash: z.string().optional(),
    last_update_pass_data: z.string().optional(),
    last_update_address: z.string().optional(),
    inn: z.string().optional(),
})
    .passthrough();
export const docDataSchema = z
    .object({
    pass_data: z.string().optional(),
    issued_by: z.string().optional(),
    issued_by_id: loose,
    issued_date: z.string().optional(),
    expiry_date: z.string().optional(),
    doc_type: z.string().optional(),
    doc_type_id: loose,
    doc_type_id_cbu: loose,
})
    .passthrough();
const registrationSchema = z
    .object({
    address: z.string().optional(),
    mfy: z.string().optional(),
    mfy_id: loose,
    region: z.string().optional(),
    region_id: loose,
    region_id_cbu: loose,
    district: z.string().optional(),
    district_id: loose,
    district_id_cbu: loose,
    country: z.string().optional(),
    country_id: loose,
    country_id_cbu: loose,
    cadastre: z.string().optional(),
    registration_date: z.string().optional(),
})
    .passthrough();
export const addressSchema = z
    .object({
    permanent_address: z.string().optional(),
    temporary_address: z.string().optional(),
    permanent_registration: registrationSchema.optional(),
    temporary_registration: registrationSchema.optional(),
})
    .passthrough();
export const contactsSchema = z
    .object({
    phone: z.string().optional(),
    email: z.string().optional(),
})
    .passthrough();
/** MyID'dan kelgan to'liq profil (`GET /api/v1/inplace/jobs/{id}` → `data.profile`). */
export const myIdProfileSchema = z
    .object({
    common_data: commonDataSchema,
    doc_data: docDataSchema.optional(),
    address: addressSchema.optional(),
    contacts: contactsSchema.optional(),
})
    .passthrough();
export const myIdUserResponseSchema = z
    .object({
    profile: myIdProfileSchema,
    /** `simple` yoki `strong`. Biz `strong` talab qilamiz. */
    authentication_method: z.string().optional(),
})
    .passthrough();
// ─── Bizning API: identifikatsiyani boshlash ────────────────────────────────
/**
 * Anketa — MyID'ga o'tishdan oldin sotuvchi to'ldiradi.
 *
 * MUHIM: MyID sessiya yaratishda quyidagi kombinatsiyalardan birini talab
 * qiladi — `pinfl + birth_date`, `pass_data + birth_date` yoki `reuid`.
 * Shu sababli tug'ilgan sana va hujjat anketada yig'iladi.
 */
export const startIdentificationRequestSchema = z
    .object({
    phone: phoneSchema,
    /** Karta MyID'dan kelmaydi — alohida yig'iladi. */
    card: cardSchema,
    birth_date: isoDateSchema,
    pinfl: pinflSchema.optional(),
    pass_data: passportSchema.optional(),
    /** Norezidentlar uchun `false`. */
    is_resident: z.boolean().default(true),
    /**
     * Oldingi verifikatsiyadan qolgan identifikator. Bo'lsa — MyID pasport
     * va tug'ilgan sanani so'ramaydi: takroriy mijoz hujjatini qayta
     * kiritmaydi.
     */
    reuid: z.string().optional(),
    /**
     * Qo'shimcha kontaktlar. Alif yangi mijoz uchun majburiy deb belgilagan,
     * shuning uchun anketa bosqichida yig'iladi — mijoz do'konda turganda.
     */
    contacts: clientContactsSchema,
    /**
     * Telefon SMS orqali tasdiqlanganini isbotlovchi belgi
     * (`POST /api/otp/verify` dan olinadi).
     *
     * VAQTINCHA MAJBURIY EMAS (mijoz qarori, 14.09.2026): anketadagi
     * SMS-tasdiq bosqichi olib turildi. Tekshiruvning o'zi joyida —
     * belgi kelsa baribir tekshiriladi. Qaytarish uchun quyidagi
     * qatorni tiklash kifoya:
     *
     *   phoneVerificationToken: z.string().uuid('v.phoneNotVerified'),
     */
    phoneVerificationToken: z.string().uuid('v.phoneNotVerified').optional(),
})
    .refine((v) => Boolean(v.pinfl) || Boolean(v.pass_data) || Boolean(v.reuid), {
    message: 'v.identityRequired',
    path: ['pinfl'],
})
    .refine((v) => !conflictsWithClientPhone(v.contacts, v.phone), {
    message: 'v.contactsOwnPhone',
    path: ['contacts'],
});
export const identifiedClientSchema = z.object({
    clientId: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    middleName: z.string().nullable(),
    birthDate: z.string().nullable(),
    /** Maskalangan: 3123******23 */
    pinflMasked: z.string(),
    passport: z.string().nullable(),
    phone: z.string(),
    cardMasked: z.string().nullable(),
    address: z.string().nullable(),
    /**
     * Fuqarolik — KOD ko'rinishida (ISO 3166-1 raqamli, `citizenship_id_cbu`).
     *
     * MyID uni matn bilan bermaydi, faqat kod. Nomga aylantirish ekranda
     * bo'ladi: sotuvchi tilni almashtirsa, so'rovni qaytarmasdan
     * tarjima qilinadi — rad sabablari bilan bir xil qoida.
     */
    citizenshipCode: z.string().nullable(),
    comparisonValue: z.number().nullable(),
    /** Provayderlarga uzatiladigan rasm havolasi. */
    photoUrl: z.string().nullable(),
});
export const startIdentificationResponseSchema = z.object({
    /** Bizning tomondagi identifikatsiya urinishi. */
    identificationId: z.string().uuid(),
    mode: z.enum(['mock', 'real']),
    /**
     * Mijoz ilgari identifikatsiyadan o'tgan bo'lsa — MyID takrorlanmaydi
     * va mijoz shu yerda qaytadi. Bunda `identificationId` ishlatilmaydi.
     *
     * Talab mijozdan: «bir marta o'tgan odam ikkinchi marta o'tmasin».
     */
    client: identifiedClientSchema.nullable().default(null),
    /** Qachon identifikatsiyadan o'tgan (takroriy mijoz uchun). */
    identifiedAt: z.string().datetime().nullable().default(null),
});
/**
 * Verifikatsiya: veb-kameradan olingan rasm yuboriladi.
 *
 * Rasmni MyID'ga biz yuklaymiz (Inplace oqimi), shuning uchun u bizda
 * qoladi va Alif'ga havola qilib beriladi.
 */
export const verifyIdentificationRequestSchema = z.object({
    /** `data:image/jpeg;base64,...` */
    photo: z
        .string()
        .regex(/^data:image\/(jpeg|jpg|png);base64,/i, 'v.photoFormat'),
    /** Faqat `mock` rejimda: xatolik stsenariysini sinash uchun. */
    mockResultCode: z.number().int().optional(),
});
/** Identifikatsiyadan keyingi mijoz kartochkasi (ekranga chiqadi). */
/**
 * Kamerani telefonga uzatish (QR).
 *
 * Sotuvchining kompyuterida kamera bo'lmasligi mumkin. Ekranda QR
 * ko'rsatiladi, mijoz uni telefonida ochadi va rasmga tushadi.
 */
export const handoffLinkSchema = z.object({
    /** Telefonda ochiladigan to'liq havola. */
    url: z.string(),
    /** Havola qancha vaqt amal qiladi, soniyada. */
    expiresInSec: z.number().int().positive(),
});
/** Telefondagi sahifa uchun: kimligi emas, faqat nima qilish kerakligi. */
export const handoffStateSchema = z.object({
    status: z.enum(['READY', 'DONE', 'EXPIRED']),
    mode: z.enum(['mock', 'real']),
    /**
     * MyID sahifasining manzili — real rejimda mijoz o'sha yerda
     * suratga tushadi. Mock rejimda `null`: kamera bizning sahifamizda.
     */
    myidUrl: z.string().nullable().default(null),
});
/** POST /api/myid/handoff/:token/myid — MyID qaytargan `auth_code`. */
export const completeMyIdRequestSchema = z.object({
    code: z.string().min(8).max(500),
});
export const verifyIdentificationResponseSchema = z.object({
    status: z.enum(['SUCCESS', 'FAILED', 'PENDING']),
    client: identifiedClientSchema.nullable(),
    /** MyID natija kodi — matni `myIdResultInfo()` dan olinadi. */
    resultCode: z.number().int().nullable(),
    message: z.string(),
    retryable: z.boolean(),
});
/**
 * Sotuvchi ekrani telefondagi jarayonni shu orqali kuzatadi.
 * `PENDING` — mijoz hali rasmga tushmagan.
 */
export const identificationStatusSchema = z.object({
    status: z.enum(['PENDING', 'SUCCESS', 'FAILED']),
    client: identifiedClientSchema.nullable(),
    resultCode: z.number().int().nullable(),
    message: z.string(),
    retryable: z.boolean(),
});
//# sourceMappingURL=myid.js.map